"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  IconAlertCircle,
  IconArrowRight,
  IconCircleCheck,
  IconClock,
  IconLoader2,
  IconMail,
  IconMapPin,
  IconPhone,
} from "@tabler/icons-react";
import { type ReactNode, useEffect, useState } from "react";
import { type UseFormRegisterReturn, useForm } from "react-hook-form";
import { COMPANY } from "@/lib/constants";
import {
  CONTACT_MESSAGE_MAX,
  CONTACT_PROJECT_LABELS,
  CONTACT_PROJECT_TYPES,
  type ContactFormInput,
  type ContactProjectType,
  contactFormSchema,
} from "@/lib/schemas/contact";
import { LogoMark } from "./logo-mark";
import { ObfuscatedEmailLink } from "./obfuscated-email";

type SubmitState = { kind: "idle" } | { kind: "success" } | { kind: "error"; message: string };

type ApiResponse = {
  ok: boolean;
  error?: string;
  fieldErrors?: Partial<Record<keyof ContactFormInput, string>>;
};

// Anciennes valeurs de type de projet : des liens #contact-… déjà publiés
// peuvent encore les porter, on les rabat sur l'offre actuelle.
const LEGACY_PROJECT_TYPES: Record<string, ContactProjectType> = {
  creation: "amenagements",
  conseils: "amenagements",
  elagage: "tailles",
};

function projectTypeFromHash(hash: string): ContactProjectType | undefined {
  const match = hash.match(/^contact-([a-z-]+)$/);
  if (!match) return undefined;
  const value = match[1];
  if ((CONTACT_PROJECT_TYPES as readonly string[]).includes(value)) {
    return value as ContactProjectType;
  }
  return LEGACY_PROJECT_TYPES[value];
}

const DEFAULT_VALUES: ContactFormInput = {
  nom: "",
  email: "",
  telephone: "",
  projectType: "entretien",
  message: "",
  website: "",
};

type InfoRow = {
  icon: typeof IconPhone;
  label: string;
  value: ReactNode;
  href?: string;
};

export function Contact() {
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: "idle" });

  const form = useForm<ContactFormInput>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: DEFAULT_VALUES,
    mode: "onBlur",
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  const projectType = watch("projectType");
  const message = watch("message") ?? "";

  useEffect(() => {
    function syncFromHash() {
      const type = projectTypeFromHash(window.location.hash.replace("#", ""));
      if (type) {
        setValue("projectType", type, { shouldDirty: false });
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
    syncFromHash();
    window.addEventListener("hashchange", syncFromHash);
    return () => window.removeEventListener("hashchange", syncFromHash);
  }, [setValue]);

  async function onSubmit(values: ContactFormInput) {
    setSubmitState({ kind: "idle" });
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const json = (await res.json().catch(() => ({}))) as ApiResponse;

      if (res.ok && json.ok) {
        setSubmitState({ kind: "success" });
        return;
      }

      if (res.status === 400 && json.fieldErrors) {
        for (const [field, msg] of Object.entries(json.fieldErrors)) {
          setError(field as keyof ContactFormInput, { type: "server", message: msg });
        }
        setSubmitState({
          kind: "error",
          message: json.error ?? "Certains champs doivent être corrigés.",
        });
        return;
      }

      if (res.status === 429) {
        setSubmitState({
          kind: "error",
          message: json.error ?? "Trop de demandes envoyées. Réessayez dans quelques minutes.",
        });
        return;
      }

      setSubmitState({
        kind: "error",
        message: json.error ?? "L'envoi a échoué. Réessayez ou appelez-nous directement.",
      });
    } catch {
      setSubmitState({
        kind: "error",
        message: "Connexion impossible. Vérifiez votre réseau et réessayez.",
      });
    }
  }

  function resetForm() {
    reset(DEFAULT_VALUES);
    setSubmitState({ kind: "idle" });
  }

  const infos: InfoRow[] = [
    {
      icon: IconPhone,
      label: "Téléphone",
      value: COMPANY.phoneDisplay,
      href: `tel:${COMPANY.phoneHref}`,
    },
    {
      icon: IconMail,
      label: "Email",
      value: <ObfuscatedEmailLink email={COMPANY.email} />,
    },
    {
      icon: IconMapPin,
      label: "Adresse",
      value: `${COMPANY.address.street}, ${COMPANY.address.postalCode} ${COMPANY.address.city}`,
    },
    {
      icon: IconClock,
      label: "Horaires",
      value: COMPANY.hoursDisplay,
    },
  ];

  return (
    <section id="contact" className="relative px-6 md:px-10 lg:px-16 pt-10 md:pt-14 pb-24 md:pb-36">
      <div className="relative mx-auto max-w-7xl">
        <div className="grid gap-12 md:gap-20 lg:grid-cols-[5fr_6fr] items-start">
          <div>
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-(--text-primary)/70 mb-4">
              Contact
            </p>
            <h1
              className="text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-text-primary"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Parlons de votre projet.
            </h1>
            <p className="mt-5 md:mt-6 text-lg text-(--text-primary)/80 max-w-md">
              Un devis gratuit, sans engagement. On vous rappelle pour comprendre votre besoin avant
              de chiffrer.
            </p>

            <ul className="mt-10 md:mt-14 space-y-5 md:space-y-6">
              {infos.map((info) => {
                const Icon = info.icon;
                const content = (
                  <div className="flex items-start gap-4">
                    <span className="flex items-center justify-center w-11 h-11 rounded-sm border border-(--text-primary)/25 bg-(--text-primary)/5 shrink-0 group-hover:border-(--text-primary)/50 group-hover:bg-(--text-primary)/10 transition-colors">
                      <Icon size={18} stroke={1.5} className="text-text-primary" aria-hidden />
                    </span>
                    <span className="flex flex-col gap-0.5 pt-1">
                      <span className="text-[11px] uppercase tracking-[0.2em] text-(--text-primary)/60">
                        {info.label}
                      </span>
                      <span className="text-base md:text-lg text-text-primary">{info.value}</span>
                    </span>
                  </div>
                );
                return (
                  <li key={info.label}>
                    {info.href ? (
                      <a href={info.href} className="block group">
                        {content}
                      </a>
                    ) : (
                      <div className="group">{content}</div>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="mt-10 pt-8 border-t border-(--text-primary)/15 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-(--text-primary)/60">
              <span className="h-px w-10 bg-(--text-primary)/40" />
              <span>Prise de contact rapide</span>
            </div>
          </div>

          <div className="relative md:-mt-8 lg:translate-x-4">
            <div className="relative bg-surface/95 backdrop-blur-xl p-6 md:p-10 lg:p-12 rounded-sm border border-(--text-primary)/12 shadow-[0_8px_24px_-12px_rgba(31,41,32,0.08)] md:shadow-[0_16px_40px_-20px_rgba(31,41,32,0.12)]">
              {/* Signature discrete : emblème en haut-droite. Decalage hors carte
                  pour effet "sceau" pose sur le coin. */}
              <LogoMark
                size={44}
                className="absolute -top-5 -right-5 md:-top-6 md:-right-6 ring-4 ring-surface shadow-sm"
              />
              {submitState.kind === "success" ? (
                <SuccessState onReset={resetForm} />
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                  <fieldset className="space-y-3" disabled={isSubmitting}>
                    <legend className="text-[11px] uppercase tracking-[0.2em] text-(--text-primary)/65 mb-3">
                      Type de projet
                    </legend>
                    <div className="flex flex-wrap gap-2">
                      {CONTACT_PROJECT_TYPES.map((value) => (
                        <label key={value} className="cursor-pointer">
                          <input
                            type="radio"
                            value={value}
                            checked={projectType === value}
                            className="sr-only peer"
                            {...register("projectType")}
                          />
                          <span className="inline-flex items-center gap-2 px-5 py-3 min-h-[44px] text-sm rounded-full border border-(--text-primary)/25 text-(--text-primary)/80 transition-colors peer-checked:bg-primary peer-checked:border-primary peer-checked:text-white hover:border-(--text-primary)/50 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary">
                            {CONTACT_PROJECT_LABELS[value]}
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  {/* Honeypot anti-bot : invisible aux humains, rempli par les
                      bots. Pas d'aria-hidden sur le wrapper : axe-core warn quand
                      un descendant focusable se trouve sous aria-hidden=true.
                      Le label "ne pas remplir" + tabIndex=-1 protege les lecteurs
                      d'ecran qui landeraient dessus par hasard. */}
                  <div className="absolute left-[-9999px] top-auto w-px h-px overflow-hidden">
                    <label htmlFor="website">Site web (ne pas remplir)</label>
                    <input
                      id="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      {...register("website")}
                    />
                  </div>

                  <div className="mt-8 grid gap-6 sm:grid-cols-2">
                    <Field
                      id="nom"
                      label="Nom"
                      placeholder="Jean Dupont"
                      required
                      autoComplete="name"
                      error={errors.nom?.message}
                      registration={register("nom")}
                      disabled={isSubmitting}
                    />
                    <Field
                      id="email"
                      type="email"
                      label="Email"
                      placeholder="vous@exemple.fr"
                      required
                      autoComplete="email"
                      error={errors.email?.message}
                      registration={register("email")}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="mt-6">
                    <Field
                      id="telephone"
                      type="tel"
                      label="Téléphone"
                      placeholder="06 12 34 56 78"
                      autoComplete="tel"
                      error={errors.telephone?.message}
                      registration={register("telephone")}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="mt-6">
                    <div className="flex items-baseline justify-between mb-2">
                      <label
                        htmlFor="message"
                        className="text-[11px] uppercase tracking-[0.2em] text-(--text-primary)/65"
                      >
                        Votre projet
                        <span className="ml-1 text-(--text-primary)/40">*</span>
                      </label>
                      <span
                        className={`text-[11px] tabular-nums ${
                          message.length > CONTACT_MESSAGE_MAX * 0.9
                            ? "text-text-primary"
                            : "text-(--text-primary)/75"
                        }`}
                        aria-live="polite"
                      >
                        {message.length} / {CONTACT_MESSAGE_MAX}
                      </span>
                    </div>
                    <textarea
                      id="message"
                      rows={4}
                      maxLength={CONTACT_MESSAGE_MAX}
                      placeholder="Entretien, taille en nuage, aménagement extérieur... parlez-nous de votre projet."
                      aria-describedby="message-hint"
                      aria-invalid={errors.message ? "true" : undefined}
                      disabled={isSubmitting}
                      {...register("message")}
                      className="w-full bg-transparent border-b border-(--text-primary)/25 focus:border-text-primary outline-none py-2.5 text-text-primary placeholder:text-(--text-primary)/35 text-base transition-colors resize-none disabled:opacity-60 aria-invalid:border-red-500"
                    />
                    {errors.message ? (
                      <p className="mt-2 text-[12px] text-red-600">{errors.message.message}</p>
                    ) : (
                      <p
                        id="message-hint"
                        className="mt-3 text-[12px] text-(--text-primary)/75 leading-relaxed max-w-[60ch]"
                      >
                        Quelques infos utiles : type de jardin, surface approximative, ville,
                        contraintes particulières...
                      </p>
                    )}
                  </div>

                  {submitState.kind === "error" ? (
                    <div
                      role="alert"
                      className="mt-6 flex items-start gap-3 p-4 rounded-sm border border-red-500/30 bg-red-50/60 text-sm text-red-800"
                    >
                      <IconAlertCircle
                        size={18}
                        stroke={1.8}
                        aria-hidden
                        className="shrink-0 mt-0.5"
                      />
                      <span>{submitState.message}</span>
                    </div>
                  ) : null}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="mt-8 w-full group inline-flex items-center justify-center gap-3 px-7 py-4 bg-primary hover:bg-primary-light text-white font-medium text-base transition-colors rounded-full disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <IconLoader2 size={18} stroke={2} aria-hidden className="animate-spin" />
                        <span>Envoi en cours...</span>
                      </>
                    ) : (
                      <>
                        <span>Demander un rendez-vous</span>
                        <IconArrowRight
                          size={18}
                          stroke={2}
                          className="group-hover:translate-x-0.5 transition-transform"
                          aria-hidden
                        />
                      </>
                    )}
                  </button>

                  <p className="mt-4 text-xs text-(--text-primary)/75 text-center">
                    En soumettant, vous acceptez d&apos;être recontacté par Symbiosis Paysage
                    Services.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SuccessState({ onReset }: { onReset: () => void }) {
  return (
    <div role="status" aria-live="polite" className="py-8 md:py-12 text-center">
      {/* Logo en signature + badge check superpose : confirme le succes ET marque
          le moment "merci de la part de Symbiosis". */}
      <div className="relative inline-block mb-6">
        <LogoMark size={72} className="ring-2 ring-(--text-primary)/15" />
        <span
          aria-hidden
          className="absolute -bottom-1 -right-1 inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary text-white shadow-sm ring-2 ring-surface"
        >
          <IconCircleCheck size={18} stroke={2} />
        </span>
      </div>
      <h3
        className="text-3xl md:text-4xl tracking-tight text-text-primary"
        style={{ fontFamily: "var(--font-display)" }}
      >
        Demande envoyée.
      </h3>
      <p className="mt-4 text-(--text-primary)/85 max-w-sm mx-auto">
        Merci. On vous recontacte rapidement pour caler un rendez-vous.
      </p>
      <button
        type="button"
        onClick={onReset}
        className="mt-8 inline-flex items-center gap-2 text-sm text-(--text-primary)/80 hover:text-text-primary underline underline-offset-4 decoration-(--text-primary)/40 hover:decoration-text-primary transition-colors"
      >
        Envoyer une autre demande
      </button>
    </div>
  );
}

type FieldProps = {
  id: string;
  label: string;
  placeholder?: string;
  type?: "text" | "email" | "tel";
  required?: boolean;
  autoComplete?: string;
  disabled?: boolean;
  error?: string;
  registration: UseFormRegisterReturn;
};

function Field({
  id,
  label,
  placeholder,
  type = "text",
  required,
  autoComplete,
  disabled,
  error,
  registration,
}: FieldProps) {
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={id}
        className="text-[11px] uppercase tracking-[0.2em] text-(--text-primary)/65"
      >
        {label}
        {required ? <span className="ml-1 text-(--text-primary)/40">*</span> : null}
      </label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        {...registration}
        className="w-full bg-transparent border-b border-(--text-primary)/25 focus:border-text-primary outline-none py-2.5 text-text-primary placeholder:text-(--text-primary)/35 text-base transition-colors disabled:opacity-60 aria-invalid:border-red-500"
      />
      {error ? (
        <p id={`${id}-error`} className="text-[12px] text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}
